/**
 * draftStateMachine.dimensions.ts — extracted dimension click cases
 * (DIMLINEAR / DIMALIGNED / DIMANGULAR / DIM3POINT / DIMRADIUS /
 * DIMDIAMETER / DIMORDINATE).
 *
 * Pure structural refactor of `advanceDraftSessionWithPoint` to keep the
 * main file under the 2000-LOC ceiling (CLAUDE.md size policy). All
 * behaviour and command shapes are preserved verbatim — see the
 * dimension contract in `geometry/create-dimension`.
 *
 * Step coverage (all 7 dimension flavours):
 *   DIMLINEAR    — first → second → line       (dimensionType 0)
 *   DIMALIGNED   — first → second → line       (dimensionType 1)
 *   DIMANGULAR   — line1 → line2 → arc-point   (dimensionType 2)
 *   DIM3POINT    — vertex → arm1 → arm2 → arc  (dimensionType 5)
 *   DIMRADIUS    — arc → dim-line              (dimensionType 4)
 *   DIMDIAMETER  — arc → centre                (dimensionType 3)
 *   DIMORDINATE  — feature → leader-end        (dimensionType 6)
 *
 * Returns `null` when the step is not a dimension step — caller falls
 * through to its own switch.
 */

import { t } from "../i18n";

import type {
  DraftPoint,
  DraftSession,
  DraftStep,
  DraftCommitResult,
} from "./draftTypes";

export function isDimensionStep(step: DraftStep): boolean {
  return (
    step === "dimension-first-point" ||
    step === "dimension-second-point" ||
    step === "dimension-line-point" ||
    step === "dimaligned-first-point" ||
    step === "dimaligned-second-point" ||
    step === "dimaligned-line-point" ||
    step === "dimangular-first-line" ||
    step === "dimangular-second-line" ||
    step === "dimangular-arc-point" ||
    step === "dim3point-vertex" ||
    step === "dim3point-first-arm" ||
    step === "dim3point-second-arm" ||
    step === "dim3point-arc-point" ||
    step === "dimradius-select-arc" ||
    step === "dimradius-dim-line" ||
    step === "dimdiameter-select-arc" ||
    step === "dimdiameter-dim-line" ||
    step === "dimordinate-feature-point" ||
    step === "dimordinate-leader-end" ||
    step === "dimbaseline-second-point" ||
    step === "dimcontinue-second-point"
  );
}

export function advanceDimensionWithPoint(
  session: DraftSession,
  point: DraftPoint,
  activeSpaceId: string,
): { nextSession: DraftSession | null; commit?: DraftCommitResult } | null {
  switch (session.step) {
    case "dimension-first-point":
      return {
        nextSession: {
          ...session,
          step: "dimension-second-point",
          points: [point],
          hoverPoint: null,
        },
      };
    case "dimension-second-point":
      return {
        nextSession: {
          ...session,
          step: "dimension-line-point",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };
    case "dimension-line-point":
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            extLine1Point: { x: session.points[0].x, y: session.points[0].y },
            extLine2Point: { x: session.points[1].x, y: session.points[1].y },
            dimensionLinePoint: { x: point.x, y: point.y },
            dimensionType: 0,
            dimensionText: "",
          },
          summary: t("summary.dimension_created"),
          commandText: "DIMENSION",
        },
      };
    case "dimaligned-first-point":
      return {
        nextSession: {
          ...session,
          step: "dimaligned-second-point",
          points: [point],
          hoverPoint: null,
        },
      };
    case "dimaligned-second-point":
      return {
        nextSession: {
          ...session,
          step: "dimaligned-line-point",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };
    case "dimaligned-line-point":
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            extLine1Point: { x: session.points[0].x, y: session.points[0].y },
            extLine2Point: { x: session.points[1].x, y: session.points[1].y },
            dimensionLinePoint: { x: point.x, y: point.y },
            dimensionType: 1,
            dimensionText: "",
          },
          summary: t("summary.dimaligned_created"),
          commandText: "DIMALIGNED",
        },
      };
    case "dimangular-first-line":
      return {
        nextSession: {
          ...session,
          step: "dimangular-second-line",
          points: [point],
          hoverPoint: null,
        },
      };
    case "dimangular-second-line":
      return {
        nextSession: {
          ...session,
          step: "dimangular-arc-point",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };
    case "dimangular-arc-point":
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            extLine1Point: { x: session.points[0].x, y: session.points[0].y },
            extLine2Point: { x: session.points[1].x, y: session.points[1].y },
            dimensionLinePoint: { x: point.x, y: point.y },
            dimensionType: 2,
            dimensionText: "",
          },
          summary: t("summary.dimangular_created"),
          commandText: "DIMANGULAR",
        },
      };
    // ── DIM3POINT (angular 3-point) — DXF dimensionType = 5 ──
    // 4-point sequence: vertex → arm 1 endpoint → arm 2 endpoint → arc location.
    // The vertex is stored in `thirdPoint` (DXF group code 15/25/35).
    case "dim3point-vertex":
      return {
        nextSession: {
          ...session,
          step: "dim3point-first-arm",
          points: [point],
          hoverPoint: null,
        },
      };
    case "dim3point-first-arm":
      return {
        nextSession: {
          ...session,
          step: "dim3point-second-arm",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };
    case "dim3point-second-arm":
      return {
        nextSession: {
          ...session,
          step: "dim3point-arc-point",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };
    case "dim3point-arc-point":
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            // session.points[0] = vertex → thirdPoint
            // session.points[1] = first arm → extLine1Point
            // session.points[2] = second arm → extLine2Point
            // current point     = arc location → dimensionLinePoint
            extLine1Point: { x: session.points[1].x, y: session.points[1].y },
            extLine2Point: { x: session.points[2].x, y: session.points[2].y },
            dimensionLinePoint: { x: point.x, y: point.y },
            thirdPoint: {
              x: session.points[0].x,
              y: session.points[0].y,
              z: 0,
            },
            dimensionType: 5,
            dimensionText: "",
          },
          summary: t("summary.dim3point_created"),
          commandText: "DIM3POINT",
        },
      };
    case "dimradius-select-arc":
      return {
        nextSession: {
          ...session,
          step: "dimradius-dim-line",
          points: [point],
          hoverPoint: null,
        },
      };
    case "dimradius-dim-line":
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            extLine1Point: { x: session.points[0].x, y: session.points[0].y },
            extLine2Point: { x: session.points[0].x, y: session.points[0].y },
            dimensionLinePoint: { x: point.x, y: point.y },
            dimensionType: 4,
            dimensionText: "",
          },
          summary: t("summary.dimradius_created"),
          commandText: "DIMRADIUS",
        },
      };
    case "dimdiameter-select-arc":
      return {
        nextSession: {
          ...session,
          step: "dimdiameter-dim-line",
          points: [point],
          hoverPoint: null,
        },
      };
    case "dimdiameter-dim-line": {
      // For DIAMETER the user picks two points:
      //   1) a point on the arc (session.points[0])
      //   2) the centre of the arc (this `point`)
      // The diameter line in AutoCAD passes through the centre and ends
      // at the diametrically opposite arc point. We derive ext2 by
      // reflecting ext1 across the centre so the renderer can draw a
      // proper diameter line with arrowheads at both arc points.
      const arcPoint = session.points[0];
      const centre = point;
      const oppositePoint = {
        x: 2 * centre.x - arcPoint.x,
        y: 2 * centre.y - arcPoint.y,
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            extLine1Point: { x: arcPoint.x, y: arcPoint.y },
            extLine2Point: { x: oppositePoint.x, y: oppositePoint.y },
            dimensionLinePoint: { x: centre.x, y: centre.y },
            dimensionType: 3,
            dimensionText: "",
          },
          summary: t("summary.dimdiameter_created"),
          commandText: "DIMDIAMETER",
        },
      };
    }
    case "dimordinate-feature-point":
      return {
        nextSession: {
          ...session,
          step: "dimordinate-leader-end",
          points: [point],
          hoverPoint: null,
        },
      };
    case "dimordinate-leader-end":
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            extLine1Point: { x: session.points[0].x, y: session.points[0].y },
            extLine2Point: { x: point.x, y: point.y },
            dimensionLinePoint: { x: point.x, y: point.y },
            dimensionType: 6,
            dimensionText: "",
          },
          summary: t("summary.dimordinate_created"),
          commandText: "DIMORDINATE",
        },
      };
    // ── DIMBASELINE — chain anchored at first ext line of last dim ──
    // session.options pre-armed by useDraftSession.beginDrafting from the
    // most recently created Dimension entity (chainExt1X/Y, chainDimLineX/Y,
    // chainDimType, chainStep). Each click commits a new dim that reuses
    // chainExt1 as the new ext1 and offsets the dim-line by DIMDLI per
    // stack-step. The session loops so multiple baselines can be chained.
    case "dimbaseline-second-point": {
      const ext1 = readChainPoint(session.options, "chainExt1X", "chainExt1Y");
      const origDimLine = readChainPoint(
        session.options,
        "chainDimLineX",
        "chainDimLineY",
      );
      const dimType = readChainNumber(session.options, "chainDimType", 0);
      if (!ext1 || !origDimLine) {
        return {
          nextSession: null,
          commit: {
            summary: t("prompt.dimbaseline.no_anchor"),
            commandText: "DIMBASELINE",
            feedOnly: true,
          },
        };
      }
      const stepIdx = readChainNumber(session.options, "chainStep", 0) + 1;
      const offset = computeBaselineOffset(
        ext1,
        point,
        origDimLine,
        stepIdx,
      );
      return {
        nextSession: {
          ...session,
          step: "dimbaseline-second-point",
          points: [],
          hoverPoint: null,
          options: {
            ...session.options,
            chainStep: String(stepIdx),
          },
        },
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            extLine1Point: { x: ext1.x, y: ext1.y },
            extLine2Point: { x: point.x, y: point.y },
            dimensionLinePoint: { x: offset.x, y: offset.y },
            dimensionType: dimType,
            dimensionText: "",
          },
          summary: t("summary.dimbaseline_created"),
          commandText: "DIMBASELINE",
        },
      };
    }
    // ── DIMCONTINUE — chain anchored at second ext line of last dim ──
    // Each new dim's ext1 = previous ext2; user clicks the new ext2.
    // Dim-line stays aligned with original. After commit chainExt1 →
    // just-clicked point so the next iteration continues monotonically.
    case "dimcontinue-second-point": {
      const ext1 = readChainPoint(session.options, "chainExt1X", "chainExt1Y");
      const origDimLine = readChainPoint(
        session.options,
        "chainDimLineX",
        "chainDimLineY",
      );
      const dimType = readChainNumber(session.options, "chainDimType", 0);
      if (!ext1 || !origDimLine) {
        return {
          nextSession: null,
          commit: {
            summary: t("prompt.dimcontinue.no_anchor"),
            commandText: "DIMCONTINUE",
            feedOnly: true,
          },
        };
      }
      return {
        nextSession: {
          ...session,
          step: "dimcontinue-second-point",
          points: [],
          hoverPoint: null,
          options: {
            ...session.options,
            chainExt1X: String(point.x),
            chainExt1Y: String(point.y),
          },
        },
        commit: {
          geometryCommand: {
            type: "geometry/create-dimension",
            source: "scene",
            spaceId: activeSpaceId,
            extLine1Point: { x: ext1.x, y: ext1.y },
            extLine2Point: { x: point.x, y: point.y },
            dimensionLinePoint: {
              x: origDimLine.x,
              y: origDimLine.y,
            },
            dimensionType: dimType,
            dimensionText: "",
          },
          summary: t("summary.dimcontinue_created"),
          commandText: "DIMCONTINUE",
        },
      };
    }
    default:
      return null;
  }
}

/**
 * Read an (x, y) point from a Record<string, string> of session options.
 * Returns null if either coordinate is missing or non-numeric.
 */
function readChainPoint(
  options: Record<string, string>,
  keyX: string,
  keyY: string,
): { x: number; y: number } | null {
  const rawX = options[keyX];
  const rawY = options[keyY];
  if (rawX === undefined || rawY === undefined) return null;
  const x = Number(rawX);
  const y = Number(rawY);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

/**
 * Read a numeric option with a fallback default.
 */
function readChainNumber(
  options: Record<string, string>,
  key: string,
  fallback: number,
): number {
  const raw = options[key];
  if (raw === undefined) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * For DIMBASELINE: compute the perpendicular-offset dimensionLinePoint.
 * DIMDLI default = 7 world units (AutoCAD default 0.38" * scale; the
 * renderer respects the dimension style's DIMDLI when redrawing). The
 * sign is chosen so successive baselines stack on the same side as the
 * original dim line.
 */
function computeBaselineOffset(
  ext1: { x: number; y: number },
  ext2: { x: number; y: number },
  origDimLine: { x: number; y: number },
  stepIdx: number,
): { x: number; y: number } {
  const DIMDLI = 7;
  const dx = ext2.x - ext1.x;
  const dy = ext2.y - ext1.y;
  const len = Math.hypot(dx, dy) || 1;
  const perpX = -dy / len;
  const perpY = dx / len;
  const toDimX = origDimLine.x - ext1.x;
  const toDimY = origDimLine.y - ext1.y;
  const side = perpX * toDimX + perpY * toDimY >= 0 ? 1 : -1;
  return {
    x: origDimLine.x + side * stepIdx * DIMDLI * perpX,
    y: origDimLine.y + side * stepIdx * DIMDLI * perpY,
  };
}
